import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ReactNode, useEffect } from 'react';

import { AccessDenied } from '../../pages/AccessDenied';
import { AccountLogin } from '../../pages/account/AccountLogin';
import { AlertList } from '../../pages/notes/Alerts/AlertList';
import { Announcement } from '../../pages/announcement/Announcement';
import { Announcements } from '../../pages/announcement/Announcements';
import AssertionDescriptorDetailWrapper from '../../pages/wrapper/AssertionDescriptorDetailWrapper';
import { AssertionDescriptorList } from '../../pages/assertiondescriptor/AssertionDescriptorList';
import BoardCaseDetailWrapper from '../../pages/wrapper/BoardCaseDetailWrapper';
import BoardCaseViewWrapper from '../../pages/wrapper/BoardCaseViewWrapper';
import BoardWrapper from '../../pages/wrapper/BoardWrapper';
import CaseEasyUpdate from '../case/easy-update/CaseEasyUpdate';
import { CaseFile } from '../../pages/case/CaseFile';
import CaseListPageWrapper from '../../pages/case/CaseListPageWrapper';
import { CaseResolutionCase } from '../../pages/quad/case-resolution/case-resolution-stepper/CaseResolutionCase';
import CheckmatePDFViewer from '../../pages/CheckmatePDFViewer';
import EntityStrategyWrapper from '../../pages/wrapper/EntityStrategyWrapper';
import { ErrorPage } from '../../pages/ErrorPage';
import ExecutionPlanWrapper from '../../pages/wrapper/ExecutionPlanWrapper';
import { ExpertCreate } from '../../pages/experts/ExpertCreate';
import { ExpertDetail } from '../../pages/experts/ExpertDetail';
import { ExpertsList } from '../../pages/experts/ExpertsList';
import { Home } from '../../pages/Home';
import { Invitation } from '../../pages/user/Invitation';
import JurisdictionStrategyDetailWrapper from '../../pages/wrapper/JurisdictionStrategyDetailWrapper';
import { KpiReport } from '../../pages/report/kpi-report/KpiReport';
import KpiViewWrapper from '../../pages/wrapper/TrendsWrapper';
import { Loader } from '../shared/Loader';
import LocalCounselStrategyDetailWrapper from '../../pages/wrapper/LocalCounselStrategyDetailWrapper';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { Logout } from '../../pages/account/Logout';
import MeetingAgendaReportWrapper from '../../pages/wrapper/MeetingAgendaReportWrapper';
import { MonthlyRecapReport } from '../../pages/report/timeline/monthly-recap/MonthlyRecapReport';
import { MyDocumentsPage } from '../../pages/documents/MyDocumentsPage';
import { NotFoundPage } from '../../pages/NotFoundPage';
import OrganizationDetailWrapper from '../../pages/wrapper/OrganizationDetailWrapper';
import { OrganizationExperienceDetail } from '../../pages/organization/OrganizationExperienceDetail';
import OrganizationExperienceDetailWrapper from '../../pages/wrapper/OrganizationExperienceDetailWrapper';
import { OrganizationExperienceList } from '../../pages/organization/OrganizationExperienceList';
import PackageSettlementDetailExportWrapper from '../../pages/wrapper/PackageSettlementDetailExportWrapper';
import PackageSettlementDetailWrapper from '../../pages/wrapper/PackageSettlementDetailWrapper';
import { PackageSettlementListWrapper } from '../../pages/case/packagesettlement/PackageSettlementList';
import PlaintiffsFirmStrategyDetailWrapper from '../../pages/wrapper/PlaintiffsFirmStrategyDetailWrapper';
import ProjectDetailWrapper from '../../pages/wrapper/ProductDetailWrapper';
import ProjectListWrapper from '../../pages/wrapper/ProjectListWrapper';
import PublicRoute from './public/PublicRoute';
import QuadAdminSecureRoute from './quad-admin/QuadAdminSecureRoute';
import { QuadDashboard } from '../../pages/quad/QuadDashboard';
import { QuadQuery } from '../../pages/quad/query/QuadQuery';
import QuadSecureRoute from './quad/QuadSecureRoute';
import { QuadTriggerRules } from '../../pages/quad/trigger-rules/QuadTriggerRules';
import { QuadZoneNotes } from '../../pages/quad/zone-notes/QuadZoneNotes';
import { QueryWrapper } from '../../pages/query/QueryWrapper';
import RecoverAccount from '../../pages/account/RecoverAccount';
import RegisterWithInvitationWrapper from '../../pages/wrapper/RegisterWithInvitationWrapper';
import RegistrationOptionWrapper from '../../pages/wrapper/RegistrationOptionWrapper';
import ReportListWrapper from '../../pages/wrapper/ReportListWrapper';
import { ResetPassword } from '../../pages/account/ResetPassword';
import { SameCaseEasyUpdatePage } from '../../pages/quad/same-case-easy-update/SameCaseEasyUpdatePage';
import { SameCasesAdminSearchPage } from '../../pages/quad/same-cases-admin/search/SameCasesAdminSearchPage';
import { SameCasesManagePage } from '../../pages/quad/same-cases-admin/manage/SameCasesManagePage';
import SecureRoute from './secure/SecureRoute';
import SnapshotWrapper from '../../pages/wrapper/SnapshotWrapper';
import StrategicPlanWrapper from '../../pages/wrapper/StrategicPlanWrapper';
import { TriggerDetailsPage } from '../../pages/triggers/TriggerDetailsPage';
import { TriggersPage } from '../../pages/triggers/TriggersPage';
import Tutorials from '../../pages/Tutorials';
import TutorialsRoute from './tutorials/TutorialsRoute';
import UserDetailWrapper from '../../pages/wrapper/UserDetailWrapper';
import { UserList } from '../../pages/user/UserList';
import { UserSettings } from '../user-settings/UserSettings';
import { ViewCaseResolutionPage } from '../../pages/quad/case-resolution/view/ViewCaseResolutionPage'
import ViewExportReportWrapper from '../../pages/wrapper/ViewExportReportWrapper';
import { ViewSameCasesPage } from '../../pages/quad/same-cases/view/ViewSameCasesPage';
import { WeeklyRecapReport } from '../../pages/report/timeline/weekly-recap/WeeklyRecapReport';
import ZoneNotesWrapper from '../../pages/wrapper/ZoneNotesWrapper';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoading } from '../../contexts/LoadingContext';
import { useZoneContext } from '../../contexts/useZoneContext';

export interface AppRoute {
    path?: string;
    element?: ReactNode;
    errorElement?: ReactNode;
    index?: boolean;
    children?: AppRoute[];
}

function renderRoutes(routes: AppRoute[]): React.ReactNode {
    return routes.map(({ path, element, errorElement, children }) => (
        <Route
            key={path || Math.random()}
            path={path}
            element={element}
            errorElement={<ErrorPage />}
        >
            {children && renderRoutes(children)}
        </Route>
    ));
}

export function CheckmateRoutes() {
    const { logout, user } = useAuthContext();
    const { setupZone } = useZoneContext();
    const { isLoading } = useLoading();
    const { key: locationKey } = useLocation();

    const localUser = user || { active: false };

    useEffect(() => {
        toast.dismiss({ containerId: 'errors' });
    }, [locationKey]);

    const quadRoutes: AppRoute = {
        path: LocalRoutes.Quad,
        element: <QuadSecureRoute />,
        children: [
            { index: true, element: <Navigate to={LocalRoutes.QuadDashboard} replace /> },
            { path: LocalRoutes.QuadDashboard, element: <QuadDashboard /> },
            {
                path: LocalRoutes.QuadCaseList,
                element: <ViewSameCasesPage key={locationKey} />,
            },
            {
                path: LocalRoutes.QuadCaseResolution,
                element: <ViewCaseResolutionPage key={locationKey} />,
            },
            { path: LocalRoutes.QuadSameCase, element: <ViewSameCasesPage key={locationKey} /> },
            {
                path: LocalRoutes.QuadZoneNoteCreate,
                element: <QuadZoneNotes key={locationKey} />,
            },
            {
                path: LocalRoutes.QuadTriggerRuleCreate,
                element: <QuadTriggerRules key={locationKey} />,
            },
            {
                path: LocalRoutes.QuadQuery,
                element: <QuadQuery key={locationKey} />,
            },
            { path: LocalRoutes.Announcement, element: <Announcement key={locationKey} /> },
            { path: LocalRoutes.Announcements, element: <Announcements key={locationKey} /> },
            { path: LocalRoutes.QuadUserSettings, element: <UserSettings /> },
            { path: LocalRoutes.QuadSameCaseEasyUpdate, element: <SameCaseEasyUpdatePage /> },
        ],
    };

    const securedRoutes: AppRoute = {
        path: LocalRoutes.Root,
        element: <SecureRoute />,
        children: [
            { path: LocalRoutes.Root, element: <Home /> },
            {
                path: LocalRoutes.Logout,
                element: <Logout onLogout={() => logout(() => setupZone('', ''))} />,
            },
            {
                path: LocalRoutes.Zone,
                children: [
                    {
                        path: `${LocalRoutes.Zone}/`,
                        element: <Home />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Tutorials}`,
                        element: <Tutorials />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.PackageSettlementDetailExport}`,
                        element: <PackageSettlementDetailExportWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Cases}`,
                        element: <CaseListPageWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.CaseFile}`,
                        element: <CaseFile />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.CaseFileNew}`,
                        element: <CaseFile />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.CaseEasyUpdate}`,
                        element: <CaseEasyUpdate key={locationKey} />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.CaseResolution}`,
                        element: <CaseResolutionCase />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.ReportTypeList}`,
                        element: <ReportListWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.ReportDetail}`,
                        element: <ViewExportReportWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Query}`,
                        element: <QueryWrapper key={locationKey} />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.TriggerNew}`,
                        element: <TriggerDetailsPage isCreate />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.TriggerDetails}`,
                        element: <TriggerDetailsPage />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Triggers}`,
                        element: <TriggersPage />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.ZoneNotes}`,
                        element: <ZoneNotesWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.WeeklyRecap}`,
                        element: <WeeklyRecapReport />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.WeeklyRecapCollab}`,
                        element: <WeeklyRecapReport />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.MonthlyRecap}`,
                        element: <MonthlyRecapReport />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.MonthlyRecapCollab}`,
                        element: <MonthlyRecapReport />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.KpiReport}`,
                        element: <KpiReport />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.KpiReportCollab}`,
                        element: <KpiReport />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.MeetingAgenda}`,
                        element: <MeetingAgendaReportWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Trends}`,
                        element: <KpiViewWrapper key={locationKey} />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Board}`,
                        element: <BoardWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.BoardCaseDetail}`,
                        element: <BoardCaseDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.BoardViewDetail}`,
                        element: <BoardCaseViewWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.PackageSettlements}`,
                        element: <PackageSettlementListWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.PackageSettlementDetails}`,
                        element: <PackageSettlementDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.PackageSettlementNew}`,
                        element: <PackageSettlementDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Experts}`,
                        element: <ExpertsList key={locationKey} />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.ExpertCreate}`,
                        element: <ExpertCreate />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.ExpertDetail}`,
                        element: <ExpertDetail />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.StrategicPlan}`,
                        element: <StrategicPlanWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.StrategyExecutionPlan}`,
                        element: <ExecutionPlanWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Strategy}`,
                        element: <EntityStrategyWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.PlaintiffFirmStrategyDetail}`,
                        element: <PlaintiffsFirmStrategyDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.JurisdictionStrategyDetail}`,
                        element: <JurisdictionStrategyDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.LocalCounselStrategyDetail}`,
                        element: <LocalCounselStrategyDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.PlaintiffFirmStrategyNew}`,
                        element: <PlaintiffsFirmStrategyDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.JurisdictionStrategyNew}`,
                        element: <JurisdictionStrategyDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.LocalCounselStrategyNew}`,
                        element: <LocalCounselStrategyDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Projects}`,
                        element: <ProjectListWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.ProjectDetail}`,
                        element: <ProjectDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.ProjectNew}`,
                        element: <ProjectDetailWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Snapshot}`,
                        element: <SnapshotWrapper />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.Alerts}`,
                        element: <AlertList key={locationKey} />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.MyDocuments}`,
                        element: <MyDocumentsPage key={locationKey} />,
                    },
                    {
                        path: `${LocalRoutes.Zone}${LocalRoutes.AccessDenied}`,
                        element: <AccessDenied />,
                    },
                    { path: '*', element: <NotFoundPage /> },
                ],
            },
            { path: LocalRoutes.UserSettings, element: <UserSettings /> },
            { path: '*', element: <NotFoundPage /> },
            { path: LocalRoutes.AccessDenied, element: <AccessDenied /> },
        ],
    };

    const quadAdminRoutes: AppRoute = {
        path: `${LocalRoutes.Quad}/admin`,
        element: <QuadAdminSecureRoute />,
        children: [
            { path: LocalRoutes.QuadAdminSameCaseManage, element: <SameCasesManagePage /> },
            {
                path: LocalRoutes.QuadAdminSameCaseSearch,
                element: <SameCasesAdminSearchPage />,
            },
        ],
    };

    const adminRoutes: AppRoute = {
        path: LocalRoutes.Root,
        element: <SecureRoute />,
        children: [
            { path: LocalRoutes.Admin, element: <Home /> },
            {
                path: LocalRoutes.CreateInvitation,
                element: <Invitation user={user ?? { active: false }} />,
            },
            {
                path: LocalRoutes.AssertionDescriptors,
                element: <AssertionDescriptorList user={localUser} />,
            },
            {
                path: LocalRoutes.AssertionDescriptorNew,
                element: <AssertionDescriptorDetailWrapper />,
            },
            {
                path: LocalRoutes.AssertionDescriptorDetail,
                element: <AssertionDescriptorDetailWrapper />,
            },
            { path: LocalRoutes.Users, element: <UserList user={localUser} /> },
            { path: LocalRoutes.UserDetail, element: <UserDetailWrapper /> },
            {
                path: LocalRoutes.Zones,
                element: <OrganizationExperienceList user={localUser} />,
            },
            {
                path: LocalRoutes.ZoneDetail,
                element: <OrganizationExperienceDetailWrapper />,
            },
            {
                path: LocalRoutes.ZoneNew,
                element: <OrganizationExperienceDetail user={localUser} />,
            },
            {
                path: LocalRoutes.OrganizationDetail,
                element: <OrganizationDetailWrapper />,
            },
            { path: '*', element: <NotFoundPage /> },
        ],
    };

    const tutorialsRoute: AppRoute = {
        path: LocalRoutes.Tutorials,
        element: <TutorialsRoute />,
        children: [
            {
                path: `${LocalRoutes.Tutorials}${LocalRoutes.ViewPDF}`,
                children: [
                    {
                        path: `${LocalRoutes.Tutorials}${LocalRoutes.ViewPDF}/:id`,
                        element: <CheckmatePDFViewer />,
                    },
                ],
            },
            { path: '*', element: <NotFoundPage /> },
        ],
    };

    const publicRoutes: AppRoute = {
        path: LocalRoutes.Root,
        element: <PublicRoute />,
        children: [
            { path: LocalRoutes.ForgotPassword, element: <RecoverAccount /> },
            { path: LocalRoutes.ResetPassword, element: <ResetPassword /> },
            { path: LocalRoutes.AcceptInvitation, element: <RegistrationOptionWrapper /> },
            {
                path: LocalRoutes.RegisterWithInvitation,
                element: <RegisterWithInvitationWrapper />,
            },
            { path: '*', element: <NotFoundPage /> },
        ],
    };

    const routes: AppRoute[] = [
        {
            path: LocalRoutes.Login,
            element: <AccountLogin />,
            children: [
                {
                    path: LocalRoutes.LoginAdvanced,
                    element: <AccountLogin />,
                },
            ],
        },
        quadRoutes,
        securedRoutes,
        quadAdminRoutes,
        adminRoutes,
        tutorialsRoute,
        publicRoutes,
        { path: '*', element: <NotFoundPage /> },
    ];

    return (
        <>
            {<Routes>{renderRoutes(routes)}</Routes>}
            {isLoading && <Loader />}
        </>
    );
}
